/**
 * Inférence du degré cible des emprunts (borrowedFromDegree) à partir du contexte.
 * Règle : pour tout bloc consécutif d'accords avec isBorrowed, le degré cible est
 * le degré du premier accord qui suit la fin de la parenthèse.
 * Ex. (V) IV → V reçoit borrowedFromDegree 'IV' ; (II V V64 V) II → les 4 reçoivent 'II'.
 */

const VALID_DEGREES = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII']

function normalizeDegree(degree) {
  if (!degree || typeof degree !== 'string') return null
  const d = degree.trim().toUpperCase().replace(/[♭#b]/, '')
  return VALID_DEGREES.includes(d) ? d : null
}

/**
 * Trouve les runs d'indices où predicate(chord) est true (consecutive).
 * @param {Array<object|null>} chords
 * @param {function} predicate
 * @returns {{ startIndex: number, endIndex: number }[]}
 */
function runGroups(chords, predicate) {
  const out = []
  let start = null
  for (let i = 0; i < chords.length; i++) {
    if (predicate(chords[i])) {
      if (start === null) start = i
    } else {
      if (start !== null) {
        out.push({ startIndex: start, endIndex: i - 1 })
        start = null
      }
    }
  }
  if (start !== null) out.push({ startIndex: start, endIndex: chords.length - 1 })
  return out
}

/**
 * Infère borrowedFromDegree pour les accords emprunt (isBorrowed) qui ne l'ont pas encore.
 * Le degré cible est pris du premier accord après chaque bloc d'emprunts.
 * @param {Array<object|null>} chords - Tableau d'objets accord (ou null), ordre des marqueurs
 * @returns {Array<object|null>} Nouveau tableau de même longueur (copies avec borrowedFromDegree rempli si inféré)
 */
export function inferBorrowedFromDegree(chords) {
  if (!Array.isArray(chords)) return chords
  const result = chords.map((c) => (c && typeof c === 'object' ? { ...c } : c))
  const groups = runGroups(result, (c) => c?.isBorrowed === true)
  for (const { startIndex, endIndex } of groups) {
    const nextIndex = endIndex + 1
    if (nextIndex >= result.length) continue
    const nextChord = result[nextIndex]
    const targetDegree = normalizeDegree(nextChord?.degree)
    if (!targetDegree) continue
    for (let i = startIndex; i <= endIndex; i++) {
      const chord = result[i]
      if (chord && chord.isBorrowed === true) {
        if (chord.borrowedFromDegree == null || chord.borrowedFromDegree === '') {
          result[i] = { ...chord, borrowedFromDegree: targetDegree }
        }
      }
    }
  }
  return result
}
